import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, MessageCircleIcon } from 'lucide-react';
import { WHATSAPP_NUMBER, whatsappLink } from '../lib/whatsapp';
import { useLanguage } from '../lib/i18n';
export function Contact() {
  const {
    t,
    lang
  } = useLanguage();
  const experienceOptions = [t('contact.opt1'), t('contact.opt2'), t('contact.opt3')];
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    experience: experienceOptions[0],
    message: ''
  });
  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const lines = [`Hola! Quiero reservar una experiencia ✨`, ``, `• Nombre: ${form.name || '—'}`, `• Experiencia: ${form.experience}`, form.phone ? `• Teléfono: ${form.phone}` : '', form.email ? `• Email: ${form.email}` : '', form.message ? `\n${form.message}` : ''].filter(Boolean).join('\n');
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return <section id="contacto" className="bg-volcanic text-ivory px-5 sm:px-8 lg:px-24 py-16 sm:py-24 lg:py-32 w-full overflow-hidden" data-id="element-22">
      <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-24 max-w-[1400px] mx-auto" data-id="element-23">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 1.2,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-24">
          <p className="uppercase tracking-[0.3em] text-olive text-[9px] sm:text-[10px] font-semibold mb-4 sm:mb-6" data-id="element-25">
            {t('contact.tag')}
          </p>
          <h2 className="font-display text-fluid-h2 leading-[1.1] font-light mb-6 sm:mb-10" data-id="element-26">
            {t('contact.title1')} <br data-id="element-27" />
            <span className="font-script text-fluid-script text-terracotta inline-block mt-[-0.1em]" data-id="element-28">
              {t('contact.title2')}
            </span>
          </h2>
          <p className="max-w-md leading-relaxed text-ivory/90 mb-8 sm:mb-12 font-light text-[14px] sm:text-[15px]" data-id="element-29">
            {t('contact.desc')}
          </p>

          <div className="space-y-4 sm:space-y-5 text-[13px] sm:text-sm" data-id="element-30">
            <div className="flex items-center gap-3 sm:gap-4 text-ivory/90" data-id="element-31">
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0" data-id="element-32">
                <CheckCircleIcon size={12} strokeWidth={2} className="text-terracotta sm:w-3.5 sm:h-3.5" data-id="element-33" />
              </div>
              <span className="font-light" data-id="element-34">{t('contact.b1')}</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 text-ivory/90" data-id="element-35">
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0" data-id="element-36">
                <CheckCircleIcon size={12} strokeWidth={2} className="text-terracotta sm:w-3.5 sm:h-3.5" data-id="element-37" />
              </div>
              <span className="font-light" data-id="element-38">{t('contact.b2')}</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 text-ivory/90" data-id="element-39">
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0" data-id="element-40">
                <CheckCircleIcon size={12} strokeWidth={2} className="text-terracotta sm:w-3.5 sm:h-3.5" data-id="element-41" />
              </div>
              <span className="font-light" data-id="element-42">{t('contact.b3')}</span>
            </div>
          </div>

          <a href={whatsappLink('contact', lang)} target="_blank" rel="noopener noreferrer" className="mt-8 sm:mt-12 inline-flex items-center justify-center gap-3 border border-ivory/30 hover:bg-ivory/10 px-6 sm:px-8 py-3.5 sm:py-4 rounded-md text-[13px] sm:text-sm transition-colors duration-300 font-light w-full sm:w-auto" data-id="element-43">
            <MessageCircleIcon size={16} strokeWidth={1.5} className="sm:w-[18px] sm:h-[18px]" data-id="element-44" />
            <span data-id="element-45">{t('contact.btnDirect')}</span>
          </a>
        </motion.div>

        <motion.form initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 1.2,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-[13px] sm:text-sm" data-id="element-46">
          <div data-id="element-47">
            <label className="block mb-2 sm:mb-3 text-[10px] sm:text-xs text-ivory/70 uppercase tracking-[0.2em] font-medium" data-id="element-48">
              {t('contact.name')}
            </label>
            <input type="text" value={form.name} onChange={update('name')} placeholder={t('contact.namePh')} className="bg-transparent border-b border-ivory/30 w-full pb-2 sm:pb-3 outline-none placeholder:text-ivory/40 focus:border-terracotta transition-colors duration-300 font-light" data-id="element-49" />
          </div>
          <div data-id="element-50">
            <label className="block mb-2 sm:mb-3 text-[10px] sm:text-xs text-ivory/70 uppercase tracking-[0.2em] font-medium" data-id="element-51">
              {t('contact.phone')}
            </label>
            <input type="tel" value={form.phone} onChange={update('phone')} placeholder={t('contact.phonePh')} className="bg-transparent border-b border-ivory/30 w-full pb-2 sm:pb-3 outline-none placeholder:text-ivory/40 focus:border-terracotta transition-colors duration-300 font-light" data-id="element-52" />
          </div>
          <div data-id="element-53">
            <label className="block mb-2 sm:mb-3 text-[10px] sm:text-xs text-ivory/70 uppercase tracking-[0.2em] font-medium" data-id="element-54">
              {t('contact.email')}
            </label>
            <input type="email" value={form.email} onChange={update('email')} placeholder={t('contact.emailPh')} className="bg-transparent border-b border-ivory/30 w-full pb-2 sm:pb-3 outline-none placeholder:text-ivory/40 focus:border-terracotta transition-colors duration-300 font-light" data-id="element-55" />
          </div>
          <div data-id="element-56">
            <label className="block mb-2 sm:mb-3 text-[10px] sm:text-xs text-ivory/70 uppercase tracking-[0.2em] font-medium" data-id="element-57">
              {t('contact.exp')}
            </label>
            <select value={form.experience} onChange={update('experience')} className="bg-transparent border-b border-ivory/30 w-full pb-2 sm:pb-3 outline-none text-ivory focus:border-terracotta transition-colors duration-300 font-light" data-id="element-58">
              {experienceOptions.map(opt => <option key={opt} className="text-volcanic bg-sand" data-id="element-59">
                  {opt}
                </option>)}
            </select>
          </div>
          <div className="md:col-span-2" data-id="element-60">
            <label className="block mb-2 sm:mb-3 text-[10px] sm:text-xs text-ivory/70 uppercase tracking-[0.2em] font-medium" data-id="element-61">
              {t('contact.msg')}
            </label>
            <textarea rows={4} value={form.message} onChange={update('message')} placeholder={t('contact.msgPh')} className="bg-transparent border-b border-ivory/30 w-full pb-2 sm:pb-3 outline-none placeholder:text-ivory/40 resize-none focus:border-terracotta transition-colors duration-300 font-light" data-id="element-62" />
          </div>
          <div className="md:col-span-2 flex justify-end pt-4 sm:pt-6" data-id="element-63">
            <button type="submit" className="inline-flex items-center justify-center gap-3 bg-terracotta hover:bg-terracottaDark transition-colors duration-300 px-8 sm:px-12 py-4 sm:py-5 rounded-md uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[10px] sm:text-xs w-full sm:w-auto" data-id="element-64">
              <MessageCircleIcon size={16} strokeWidth={1.5} className="sm:w-[18px] sm:h-[18px]" data-id="element-65" />
              <span data-id="element-66">{t('contact.btn')}</span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>;
}